package com.mgacreative.hotelru.feature.room.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.automirrored.filled.List
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.lazy.rememberLazyListState
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun RoomSummaryScreen(
    onNavigate: (String) -> Unit = {},
    viewModel: RoomSummaryViewModel = koinViewModel()
) {
    val globalSummary by viewModel.globalSummary.collectAsState()
    val typeSummaries by viewModel.roomTypeSummaries.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()
    val listState = rememberLazyListState()

    LaunchedEffect(Unit) {
        viewModel.loadSummaryData()
    }

    Scaffold(
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        bottomBar = {
            RoomSummaryBottomNavBar(onNavigate = onNavigate)
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF8FAFC))
        ) {
            // Modern Clean Top Bar
            Surface(
                color = Color.White,
                tonalElevation = 1.dp,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(
                            onClick = { onNavigate("DashboardScreen") },
                            modifier = Modifier.size(36.dp)
                        ) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Back"), tint = Color(0xFF0F172A))
                        }
                        Spacer(Modifier.width(8.dp))
                        Column {
                            Text(
                                text = l10n("Room Summary & Status Stats"),
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF0F172A),
                                fontSize = 17.sp
                            )
                            Text(
                                text = l10n("Anlık doluluk ve oda tipi dağılım raporu"),
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    OutlinedButton(
                        onClick = { viewModel.loadSummaryData() },
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 10.dp, vertical = 4.dp),
                        modifier = Modifier.height(34.dp)
                    ) {
                        Icon(Icons.Default.Refresh, contentDescription = l10n("Refresh"), modifier = Modifier.size(16.dp), tint = Color(0xFF2563EB))
                        Spacer(Modifier.width(4.dp))
                        Text(l10n("Refresh"), fontSize = 12.sp, color = Color(0xFF2563EB), fontWeight = FontWeight.SemiBold)
                    }
                }
            }

            if (isLoading) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = Color(0xFF2563EB))
                }
            } else {
                LazyColumn(
                    state = listState,
                    modifier = Modifier.fillMaxSize().verticalScrollbar(listState),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    // Hero Global Occupancy Card
                    item {
                        GlobalSummaryCard(globalSummary)
                    }

                    // Section Title
                    item {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Box(
                                    modifier = Modifier
                                        .size(28.dp)
                                        .clip(RoundedCornerShape(6.dp))
                                        .background(Color(0xFFEFF6FF)),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Icon(Icons.Default.Hotel, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(16.dp))
                                }
                                Spacer(Modifier.width(8.dp))
                                Text(
                                    text = l10n("Room Type Distribution"),
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 15.sp,
                                    color = Color(0xFF0F172A)
                                )
                            }
                            Text(
                                text = "${typeSummaries.size} " + l10n("Oda Tipi"),
                                fontSize = 12.sp,
                                fontWeight = FontWeight.Medium,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    // Room Type Cards
                    items(typeSummaries, key = { it.roomType }) { typeSummary ->
                        RoomTypeSummaryCard(summary = typeSummary)
                    }

                    item {
                        Spacer(modifier = Modifier.height(16.dp))
                    }
                }
            }
        }
    }
}

@Composable
fun GlobalSummaryCard(summary: GlobalRoomSummary) {
    val occupancyPercent = kotlin.math.round(summary.occupancyRatePercentage).toInt()
    val occBadgeColor = when {
        occupancyPercent >= 80 -> Color(0xFFDC2626)
        occupancyPercent >= 40 -> Color(0xFFD97706)
        else -> Color(0xFF16A34A)
    }
    val occBadgeBg = when {
        occupancyPercent >= 80 -> Color(0xFFFEE2E2)
        occupancyPercent >= 40 -> Color(0xFFFEF3C7)
        else -> Color(0xFFDCFCE7)
    }

    Surface(
        shape = RoundedCornerShape(16.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(18.dp)) {
            // Header
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column {
                    Text(
                        text = l10n("GENERAL HOTEL OCCUPANCY STATUS").uppercase(),
                        fontWeight = FontWeight.Bold,
                        fontSize = 11.sp,
                        color = Color(0xFF64748B),
                        letterSpacing = 0.5.sp
                    )
                    Spacer(Modifier.height(2.dp))
                    Text(
                        text = "${summary.totalRooms} " + l10n("Total Rooms"),
                        fontWeight = FontWeight.Bold,
                        fontSize = 22.sp,
                        color = Color(0xFF0F172A)
                    )
                }
                
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(20.dp))
                        .background(occBadgeBg)
                        .padding(horizontal = 14.dp, vertical = 6.dp)
                ) {
                    Text(
                        text = "${l10n("Occupancy")}: %$occupancyPercent",
                        fontWeight = FontWeight.Bold,
                        color = occBadgeColor,
                        fontSize = 13.sp
                    )
                }
            }

            Spacer(Modifier.height(16.dp))

            // Multi-segment Visual Capacity Bar
            MultiSegmentProgressBar(
                occupied = summary.totalOccupied,
                reserved = summary.totalReserved,
                empty = summary.totalEmpty,
                total = summary.totalRooms
            )

            Spacer(Modifier.height(18.dp))

            // 3 Stat Metric Cards
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                SummaryStatBox(
                    title = l10n("Full"),
                    count = summary.totalOccupied,
                    bgColor = Color(0xFFFEF2F2),
                    borderColor = Color(0xFFFECACA),
                    textColor = Color(0xFFDC2626),
                    icon = Icons.Default.MeetingRoom,
                    modifier = Modifier.weight(1f)
                )

                SummaryStatBox(
                    title = l10n("Reserved"),
                    count = summary.totalReserved,
                    bgColor = Color(0xFFFFFBEB),
                    borderColor = Color(0xFFFDE68A),
                    textColor = Color(0xFFD97706),
                    icon = Icons.Default.Bookmark,
                    modifier = Modifier.weight(1f)
                )

                SummaryStatBox(
                    title = l10n("Empty"),
                    count = summary.totalEmpty,
                    bgColor = Color(0xFFF0FDF4),
                    borderColor = Color(0xFFBBF7D0),
                    textColor = Color(0xFF16A34A),
                    icon = Icons.Default.CheckCircle,
                    modifier = Modifier.weight(1f)
                )
            }
        }
    }
}

@Composable
fun MultiSegmentProgressBar(
    occupied: Int,
    reserved: Int,
    empty: Int,
    total: Int
) {
    if (total <= 0) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(10.dp)
                .clip(RoundedCornerShape(5.dp))
                .background(Color(0xFFE2E8F0))
        )
        return
    }

    val occWeight = occupied.toFloat() / total
    val resWeight = reserved.toFloat() / total
    val empWeight = empty.toFloat() / total

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(10.dp)
            .clip(RoundedCornerShape(5.dp))
            .background(Color(0xFFF1F5F9))
    ) {
        if (occWeight > 0f) {
            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .weight(occWeight)
                    .background(Color(0xFFDC2626))
            )
        }
        if (resWeight > 0f) {
            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .weight(resWeight)
                    .background(Color(0xFFF59E0B))
            )
        }
        if (empWeight > 0f) {
            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .weight(empWeight)
                    .background(Color(0xFF16A34A))
            )
        }
    }
}

@Composable
fun SummaryStatBox(
    title: String,
    count: Int,
    bgColor: Color,
    borderColor: Color,
    textColor: Color,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    modifier: Modifier = Modifier
) {
    Surface(
        shape = RoundedCornerShape(12.dp),
        color = bgColor,
        border = androidx.compose.foundation.BorderStroke(1.dp, borderColor),
        modifier = modifier
    ) {
        Column(
            modifier = Modifier.padding(vertical = 12.dp, horizontal = 8.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Icon(icon, contentDescription = null, tint = textColor, modifier = Modifier.size(20.dp))
            Spacer(Modifier.height(4.dp))
            Text(text = count.toString(), fontWeight = FontWeight.Bold, fontSize = 20.sp, color = textColor)
            Text(text = title, fontWeight = FontWeight.SemiBold, fontSize = 11.sp, color = textColor)
        }
    }
}

@Composable
fun RoomTypeSummaryCard(summary: RoomTypeSummary) {
    Surface(
        shape = RoundedCornerShape(14.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            // Header Row: Room Type & Total Rooms
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(36.dp)
                            .clip(RoundedCornerShape(8.dp))
                            .background(Color(0xFFEFF6FF)),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(Icons.Default.Hotel, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(18.dp))
                    }
                    Spacer(Modifier.width(10.dp))
                    Column {
                        Text(
                            text = l10n(summary.roomType),
                            fontWeight = FontWeight.Bold,
                            fontSize = 15.sp,
                            color = Color(0xFF0F172A)
                        )
                        Text(
                            text = "${summary.totalRooms} " + l10n("Rooms"),
                            fontSize = 12.sp,
                            color = Color(0xFF64748B)
                        )
                    }
                }

                // Available pill
                val isSoldOut = summary.emptyCount <= 0 && summary.totalRooms > 0
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(6.dp))
                        .background(if (isSoldOut) Color(0xFFFEE2E2) else Color(0xFFDCFCE7))
                        .padding(horizontal = 8.dp, vertical = 4.dp)
                ) {
                    Text(
                        text = if (isSoldOut) l10n("DOLU") else "${summary.emptyCount} " + l10n("Müsait"),
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Bold,
                        color = if (isSoldOut) Color(0xFFDC2626) else Color(0xFF16A34A)
                    )
                }
            }

            Spacer(Modifier.height(12.dp))

            // Mini Segmented Progress Bar
            MultiSegmentProgressBar(
                occupied = summary.occupiedCount,
                reserved = summary.reservedCount,
                empty = summary.emptyCount,
                total = summary.totalRooms
            )

            Spacer(Modifier.height(12.dp))
            HorizontalDivider(color = Color(0xFFF1F5F9))
            Spacer(Modifier.height(10.dp))

            // 3 Stat Indicator Badges
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(modifier = Modifier.size(8.dp).clip(CircleShape).background(Color(0xFFDC2626)))
                    Spacer(Modifier.width(6.dp))
                    Text(l10n("Full") + ": ", fontSize = 12.sp, color = Color(0xFF64748B))
                    Text("${summary.occupiedCount}", fontWeight = FontWeight.Bold, fontSize = 13.sp, color = Color(0xFFDC2626))
                }

                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(modifier = Modifier.size(8.dp).clip(CircleShape).background(Color(0xFFF59E0B)))
                    Spacer(Modifier.width(6.dp))
                    Text(l10n("Reserved") + ": ", fontSize = 12.sp, color = Color(0xFF64748B))
                    Text("${summary.reservedCount}", fontWeight = FontWeight.Bold, fontSize = 13.sp, color = Color(0xFFD97706))
                }

                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(modifier = Modifier.size(8.dp).clip(CircleShape).background(Color(0xFF16A34A)))
                    Spacer(Modifier.width(6.dp))
                    Text(l10n("Empty") + ": ", fontSize = 12.sp, color = Color(0xFF64748B))
                    Text("${summary.emptyCount}", fontWeight = FontWeight.Bold, fontSize = 13.sp, color = Color(0xFF16A34A))
                }
            }
        }
    }
}

@Composable
fun RoomSummaryBottomNavBar(onNavigate: (String) -> Unit) {
    NavigationBar(
        containerColor = Color.White,
        tonalElevation = 4.dp
    ) {
        NavigationBarItem(
            icon = { Icon(Icons.AutoMirrored.Filled.List, null) },
            label = { Text(l10n("Reservation"), fontSize = 10.sp) },
            selected = false,
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2563EB),
                selectedTextColor = Color(0xFF2563EB),
                indicatorColor = Color(0xFFEFF6FF)
            ),
            onClick = { onNavigate("NewReservationCalendarScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.DateRange, null) },
            label = { Text(l10n("Occupancy"), fontSize = 10.sp) },
            selected = false,
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2563EB),
                selectedTextColor = Color(0xFF2563EB),
                indicatorColor = Color(0xFFEFF6FF)
            ),
            onClick = { onNavigate("ReservationCalendarScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.AutoMirrored.Filled.ArrowForward, null) },
            label = { Text(l10n("Check-in"), fontSize = 10.sp) },
            selected = false,
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2563EB),
                selectedTextColor = Color(0xFF2563EB),
                indicatorColor = Color(0xFFEFF6FF)
            ),
            onClick = { onNavigate("CheckInListScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.Home, null) },
            label = { Text(l10n("In Use"), fontSize = 10.sp) },
            selected = false,
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2563EB),
                selectedTextColor = Color(0xFF2563EB),
                indicatorColor = Color(0xFFEFF6FF)
            ),
            onClick = { onNavigate("InUseRoomsScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.Assessment, null) },
            label = { Text(l10n("Room Summary"), fontSize = 10.sp, fontWeight = FontWeight.Bold) },
            selected = true,
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2563EB),
                selectedTextColor = Color(0xFF2563EB),
                indicatorColor = Color(0xFFEFF6FF)
            ),
            onClick = { onNavigate("RoomSummaryScreen") }
        )
    }
}
